import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  SxProps,
  Theme,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export interface ActionDropdownItem {
  id: string;
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  /** When true, the action is not clickable */
  disabled?: boolean;
}

export interface ActionDropdownMAIQProps {
  /** Button label text (e.g. "Actions") - comes from parent */
  buttonLabel: string;
  /** List of actions in the dropdown - id, label, onClick, disabled, optional icon */
  actions: ActionDropdownItem[];
  /** Tooltip for the trigger button */
  tooltip?: string;
  /** Button color */
  color?: 'primary' | 'secondary';
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** Additional sx for the trigger button */
  sx?: SxProps<Theme>;
  /** aria-label for accessibility */
  ariaLabel?: string;
}

const ActionDropdownMAIQ: React.FC<ActionDropdownMAIQProps> = ({
  buttonLabel,
  actions,
  tooltip,
  color = 'primary',
  size = 'medium',
  sx,
  ariaLabel,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action: ActionDropdownItem) => {
    if (action.disabled) return;
    action.onClick();
    handleClose();
  };

  const buttonSx: SxProps<Theme> = {
    textTransform: 'none',
    ...(sx as object),
  };

  return (
    <>
      <Tooltip title={tooltip ?? buttonLabel}>
        <Button
          id="action-dropdown-button"
          aria-label={ariaLabel ?? buttonLabel}
          aria-controls={open ? 'action-dropdown-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          variant="contained"
          color={color}
          size={size}
          endIcon={<ArrowDropDownIcon />}
          sx={buttonSx}
        >
          {buttonLabel}
        </Button>
      </Tooltip>
      <Menu
        id="action-dropdown-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'action-dropdown-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.id}
            onClick={() => handleActionClick(action)}
            disabled={action.disabled === true}
          >
            {action.icon != null && (
              <span style={{ marginRight: 8, display: 'inline-flex', alignItems: 'center' }}>
                {action.icon}
              </span>
            )}
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ActionDropdownMAIQ;
